/* eslint-disable global-require */
import React from 'react';
import { compose } from 'redux';
import { Navigate } from 'react-router-dom';
import App from './modules/App/App';

// Import higher order components
import RequireSubscription from './components/RequireSubscription';
import SubscriptionValidation from './components/SubscriptionValidation';
import RequireDashboards from './components/RequireDashboards';
import RequireAuth from './components/RequireAuth';
import ErrorBoundary from './components/ErrorBoundary';
import CheckPartner from './components/CheckPartner';
import RequireManageUsers from './components/RequireManageUsers';
import RequireManageBilling from './components/RequireManageBilling';
import RequireActiveSubscription from './components/RequireActiveSubscription';
import RequireCreateDashboards from './components/RequireCreateDashboards';
import RequireDashboardAccess from './components/RequireDashboardAccess';
import RequireActivePremium from './components/RequireActivePremium';
import RequireAiInsights from './components/RequireAiInsights';
import RequirePremiumOff from './components/RequirePremiumOff';
import RequireAkb from './components/RequireAkb';
import RequireFileApp from './components/RequireFileApp';
import RequireEnableTenant from './components/RequireEnableTenant';
import RequireStreamAccess from './components/RequireStreamAccess';
import RequirePortfolioAccess from './components/RequirePortfolioAccess';
import RequireStandardPlanForSubscribe from './components/RequireStandardPlanForSubscribe';
import RequireStandardPlanForUpgrade from './components/RequireStandardPlanForUpgrade';

//Import pages
//auth pages
import Login from './modules/Auth/pages/LoginPage';
import Register from './modules/Auth/pages/RegisterPage';
import SelectHost from './modules/Auth/pages/SelectHostPage';
import Forgot from './modules/Auth/pages/ForgotPage';
import Reset from './modules/Auth/pages/ResetPage';
import Complete from './modules/Auth/pages/CompletePage/CompletePage';
import NotFound from './modules/Common/pages/NotFoundPage';
import GeneralError from './modules/Common/pages/GeneralError';
import AuthGeneralError from './modules/Common/pages/AuthGeneralError';
import GenericMessagePage from './modules/Common/pages/GenericMessagePage';
import Downtime from './modules/Common/pages/Downtime';
import WelcomePage from './modules/Common/pages/WelcomePage';
import ErrorPage from './modules/Common/pages/ErrorPage';
import AwaitingPage from './modules/Common/pages/AwaitingPage';
import TrialPage from './modules/Common/pages/TrialPage';
import AkbPage from './modules/Common/pages/AkbPage';
import ChatPage from './modules/Common/pages/ChatPage';
import NaveAddonPage from './modules/Common/pages/NaveAddonPage';
import McpDocsPage from './modules/Common/pages/McpDocsPage';
import ApiReferencePage from './modules/Common/pages/ApiReferencePage';
import ConsentPage from './modules/Common/pages/ConsentPage';

import CreateDashboard from './modules/CreateDashboard/CreateDashboard';
import Users from './modules/Users/Users';
import Dashboards from './modules/Dashboards/Dashboards';
import Permissions from './modules/Permissions/Permissions';
import Terms from './modules/Terms/pages/Terms';
import Wallboard from './modules/Wallboard/Wallboard';
import Wallboards from './modules/Wallboards/Wallboards';
import Overview from './modules/Overview/pages/Overview';
import ProcessImprovement from './modules/ProcessImprovement/pages/ProcessImprovement';
import CFD from './modules/CFD/pages/CFD';
import CTSP from './modules/CycleTimeScatterPlot/pages/CTSP';
import CTB from './modules/CycleTimeBreakdown/pages/CTB';
import CTH from './modules/CycleTimeHistogram/pages/CTH';
import CTA from './modules/CycleTimeAging/pages/CTA';
import Throughput from './modules/Throughput/pages/Throughput';
import ThroughputHistogram from './modules/ThroughputHistogram/pages/ThroughputHistogram';
import FlowEfficiency from './modules/FlowEfficiency/pages/FlowEfficiency';
import DueDatePerf from './modules/DueDatePerformance/pages/DueDatePerformance';
import MonteCarloDates from './modules/MonteCarlo/pages/MonteCarloDates';
import MonteCarloTasks from './modules/MonteCarloTasks/pages/MonteCarloTasks';
import Subscribe from './modules/Subscribe/pages/Subscribe';
import Upgrade from './modules/Subscribe/pages/Upgrade';
import DigitalUpgrade from './modules/DigitalProgram/pages/DigitalUpgrade';
import Billing from './modules/Billing/pages/Billing';
import LimitReached from './modules/Common/pages/LimitReached';
import RemoveDashboards from './modules/Common/pages/RemoveDashboards';
import Reports from './modules/ReportConfig/Reports';
import ReportConfig from './modules/ReportConfig/ReportConfig';
import Portfolios from './modules/Portfolios/Portfolios';
import PortfolioConfig from './modules/Portfolios/PortfolioConfig';
import AllForecasts from './modules/Forecasting/pages/AllForecasts';
import ForecastingDashboard from './modules/Forecasting/pages/ForecastingDashboard';

import SsoConfig from './modules/Sso/pages/SsoConfig';
import TenantConfig from './modules/Tenant/pages/TenantConfig';

const SafeNotFound = () => {
  return typeof window === 'undefined' ?
    <NotFound /> :
    <Navigate to="/404" replace />
}

const SafeNavigate = (url) => () => {
  return typeof window === 'undefined' ?
    <NotFound /> :
    <Navigate to={url} replace />
}

export const routes = [
  {
    path: "/",
    Component: ErrorBoundary(App),
    children: [
      { index: true, Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(Overview) },
      { path: "sso/config", Component: compose(RequireAuth, RequireManageUsers, RequireFileApp)(SsoConfig) },
      { path: "login", Component: () => <Login /> },
      { path: "sso/login/:accountId", Component: () => <Login /> },
      { path: "register", Component: () => <Register /> },
      { path: "hosts", Component: () => <SelectHost /> },
      { path: "forgotpassword", Component: () => <Forgot /> },
      { path: "reset/:token", Component: () => <Reset /> },
      { path: "setup", Component: () => <Complete /> },
      { path: "terms", Component: () => <Terms /> },
      { path: "welcome", Component: () => <WelcomePage /> },
      { path: "error", Component: () => <ErrorPage /> },
      { path: "addon", Component: () => <NaveAddonPage /> },
      { path: "mcp-docs", Component: compose(RequireSubscription, RequireAuth)(McpDocsPage) },
      { path: 'api-reference', Component: SafeNavigate('/api-reference/teams') },
      { path: 'api-reference/:api', Component: compose(RequireSubscription, RequireAuth)(ApiReferencePage) },
      { path: "consent/:id", Component: compose(RequireSubscription, RequireAuth)(ConsentPage) },
      { path: "awaiting", Component: compose(RequireAuth)(AwaitingPage) },
      { path: "awaiting/sso", Component: compose(RequireAuth)(AwaitingPage) },
      { path: "akb", Component: compose(RequireAuth, RequireAkb)(AkbPage) },
      { path: "chat", Component: compose(RequireAuth)(ChatPage) },
      { path: "users", Component: compose(RequireAuth, RequireManageUsers)(Users) },
      { path: "dashboards/:id?", Component: compose(RequireAuth, RequireManageUsers, RequireDashboardAccess)(Dashboards) },
      { path: "free", Component: compose(RequireAuth, RequireDashboards)(RemoveDashboards) },
      { path: "permissions/:id", Component: compose(RequireAuth, RequireManageUsers, RequireDashboardAccess)(Permissions) },
      { path: "subscribe", Component: compose(RequireAuth, RequireManageBilling, CheckPartner, RequireStandardPlanForSubscribe)(Subscribe) },
      { path: "upgrade", Component: compose(RequireAuth, RequireManageBilling, CheckPartner, RequireStandardPlanForUpgrade)(Upgrade) },
      { path: "digital", Component: compose(RequireAuth, RequireManageBilling, CheckPartner)(DigitalUpgrade) },
      { path: "billing", Component: compose(RequireAuth, RequireManageBilling, CheckPartner, RequireActiveSubscription)(Billing) },
      { path: "wallboard/:id", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(Wallboard) },
      { path: "settings/:id", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(Wallboards) },
      { path: "forecasts/:dashboardId", Component: compose(RequireActivePremium, RequireDashboards, RequireAuth)(AllForecasts) },
      { path: "/dashboard/:dashboardId/forecasts", Component: compose(RequireActivePremium, RequireDashboards, RequireAuth)(AllForecasts) },
      { path: "forecasting/:dashboardId/:id", Component: compose(RequireActivePremium, RequireDashboards, RequireAuth)(ForecastingDashboard) },
      { path: "report/:id?", Component: compose(RequireActivePremium, RequireDashboards, RequireAuth, RequireStreamAccess)(Reports) },
      { path: "portfolios/:id?", Component: compose(RequireActivePremium, RequireAuth, RequirePortfolioAccess)(Portfolios) },
      { path: "portfolio/:id?", Component: compose(RequireActivePremium, RequireDashboards, RequireAuth, RequirePortfolioAccess)(PortfolioConfig) },
      { path: "configure/:id?", Component: compose(RequireActivePremium, RequireDashboards, RequireAuth, RequireStreamAccess)(ReportConfig) },
      { path: "premium", Component: compose(RequirePremiumOff, RequireDashboards, RequireAuth)(TrialPage) },
      { path: "expired", Component: () => <GenericMessagePage /> },
      { path: "cancelled", Component: () => <GenericMessagePage /> },
      { path: "unlicensed", Component: () => <GenericMessagePage /> },
      { path: "hosts-not-allowed", Component: () => <GenericMessagePage /> },
      { path: "no-dashboards", Component: () => <GenericMessagePage /> },
      { path: "no-streams", Component: () => <GenericMessagePage /> },
      { path: "no-portfolios", Component: () => <GenericMessagePage /> },
      { path: "no-manage-billing", Component: () => <GenericMessagePage /> },
      { path: "no-enough-streams", Component: () => <GenericMessagePage /> },
      { path: "no-create-dashboard-permissions", Component: () => <GenericMessagePage /> },
      { path: "no-value-stream-permissions", Component: () => <GenericMessagePage /> },
      { path: "404", Component: () => <NotFound /> },
      { path: "401", Component: () => <AuthGeneralError /> },
      { path: "500", Component: () => <GeneralError /> },
      { path: "downtime", Component: () => <Downtime /> },
      { path: "limitreached", Component: () => <LimitReached /> },
      { path: "create", Component: compose(RequireSubscription, RequireAuth, SubscriptionValidation, RequireCreateDashboards)(CreateDashboard) },
      { path: "update/:id", Component: compose(RequireSubscription, RequireAuth)(CreateDashboard) },
      { path: "/dashboard/:dashboardId", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(Overview) },
      { path: "/dashboard/:dashboardId/process", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(ProcessImprovement) },
      { path: "/cfd", Component: compose(RequireDashboards, RequireAuth)(CFD) },
      { path: "/dashboard/:dashboardId/cfd", Component: compose(RequireDashboards, RequireAuth)(CFD) },
      { path: "/ctsp", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTSP) },
      { path: "/dashboard/:dashboardId/ctsp", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTSP) },
      { path: "/ctb", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTB) },
      { path: "/dashboard/:dashboardId/ctb", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTB) },
      { path: "/cth", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTH) },
      { path: "/dashboard/:dashboardId/cth", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTH) },
      { path: "/cta", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTA) },
      { path: "/dashboard/:dashboardId/cta", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(CTA) },
      { path: "/trc", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(Throughput) },
      { path: "/dashboard/:dashboardId/trc", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(Throughput) },
      { path: "/th", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(ThroughputHistogram) },
      { path: "/dashboard/:dashboardId/th", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(ThroughputHistogram) },
      { path: "/fe", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(FlowEfficiency) },
      { path: "/dashboard/:dashboardId/fe", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(FlowEfficiency) },
      { path: "/ddp", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(DueDatePerf) },
      { path: "/dashboard/:dashboardId/ddp", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(DueDatePerf) },
      { path: "/montecarlodates", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(MonteCarloDates) },
      { path: "/dashboard/:dashboardId/montecarlodates", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(MonteCarloDates) },
      { path: "/montecarlotasks", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(MonteCarloTasks) },
      { path: "/dashboard/:dashboardId/montecarlotasks", Component: compose(RequireSubscription, RequireDashboards, RequireAuth)(MonteCarloTasks) },
      { path: "/tenant/config", Component: compose(RequireSubscription, RequireAuth, RequireManageBilling, RequireEnableTenant)(TenantConfig) },
      { path: "*", Component: SafeNotFound },
    ],
  },
];
